import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { ACCOUNT_QUERY_KEYS } from '@/lib/constants/account';
import { AccountApi } from '../AccountApi';
import { AccountUtils } from '../AccountUtils';
import { setAccountBillingPlanType } from '../useAccountPermissions';
import type { ICurrentBillingPlanView, IBillingAccountViewData } from '@/lib/types/Billing';

/** Fetches the current billing plan for the parent account. */
export function useBillingPlan() {
  const { authData, customerInfo } = useAuth();
  const parentCId = AccountUtils.resolveParentCId(customerInfo, authData);
  const hasBillingRead = authData?.cpuser.permissions.billingRead ?? false;
  const isFromParentAccount = AccountUtils.isFromParentAccount(authData, customerInfo);

  const query = useQuery<ICurrentBillingPlanView>({
    queryKey: ACCOUNT_QUERY_KEYS.billingPlan(parentCId ?? 0),
    queryFn: () => AccountApi.getBillingPlan(parentCId!),
    enabled: parentCId != null && hasBillingRead && isFromParentAccount,
    staleTime: 5 * 60 * 1000,
  });

  // Update the cached billing plan type for permission checks
  useEffect(() => {
    if (query.data) {
      setAccountBillingPlanType(query.data.billingPlan.billingPlanType);
    }
  }, [query.data]);

  return {
    billingPlan: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}

/** Fetches the default payment method for the parent account. */
export function usePaymentMethod() {
  const { authData, customerInfo } = useAuth();
  const parentCId = AccountUtils.resolveParentCId(customerInfo, authData);

  const query = useQuery<IBillingAccountViewData>({
    queryKey: ACCOUNT_QUERY_KEYS.paymentMethod(parentCId ?? 0),
    queryFn: () => AccountApi.getPaymentMethod(parentCId!),
    enabled: parentCId != null,
    staleTime: 5 * 60 * 1000,
  });

  return {
    paymentMethod: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}

/** Combined hook for billing page (plan + payment method). */
export function useBilling() {
  const billingPlan = useBillingPlan();
  const paymentMethod = usePaymentMethod();

  const isTrial = billingPlan.billingPlan?.billingPlan.billingPlanType === 'trial';

  return {
    ...billingPlan,
    paymentMethod: paymentMethod.paymentMethod,
    isPaymentMethodLoading: paymentMethod.isLoading,
    isPaymentMethodError: paymentMethod.isError,
    isTrial,
  };
}
